import { memo, useMemo, useState, useCallback, useEffect } from "react"
import { useTranslation } from "react-i18next"
import dayjs from "dayjs"
import type { DbObject } from "@/db/schema"
import type { DbStartAtIndex, DbReminder } from "@/db/schema"
import { StartAtType, StartAtSubType } from "@/db/schema"
import { cn } from "@/shared/utils/general/cn"
import { scheduleTask } from "@/db/operations"
import { DatePickerSheet } from "@/features/schedule/DatePickerSheet"
import { DetailContainer } from "./DetailContainer"
import { TASK_DETAIL_EVENTS } from "../hooks/useTaskDetailShortcuts"
import FlagIcon from "@/shared/icons/totodoicons/stroke/flag.svg?react"
import MoonIcon from "@/shared/icons/totodoicons/solid/moon.svg?react"
import NotificationIcon from "@/shared/icons/totodoicons/stroke/notification.svg?react"
import CalendarUpcomingIcon from "@/shared/icons/totodoicons/stroke/calendar-upcoming.svg?react"
import StarIcon from "@/shared/icons/totodoicons/stroke/star.svg?react"
import CalendarTodayIcon from "@/shared/icons/totodoicons/stroke/calendar-today.svg?react"
import { parseDbDate } from "@totodo/operations"

interface DetailDatesProps {
  task: DbObject
  startAtData?: DbStartAtIndex
  existingReminder?: DbReminder
  /** All active reminders for the task (sorted by fireAt) */
  allReminders?: DbReminder[]
  /** Number of active reminders for badge display */
  reminderCount?: number
  /** Quick-action icons rendered on the right side of the bar */
  quickActions?: React.ReactNode
  /** Show clickable container picker (Things 3 style) */
  showContainer?: boolean
}

/**
 * Date bar — v1 style inline text with icons.
 *
 * Layout: ⭐ Today   🚩 thu, 05 mar   9 days left   🔔 14:30
 *
 * §4: Dual-overdue — startAt-overdue (muted yellow) vs deadline-overdue (red)
 * §5: Evening — moon icon with "This evening"
 * Separated from content above by a top border.
 * Clicking opens the WhenSheet.
 */
export const DetailDates = memo(function DetailDates({
  task,
  startAtData,
  existingReminder,
  allReminders = [],
  reminderCount = 0,
  quickActions,
  showContainer = false,
}: DetailDatesProps) {
  const [showWhen, setShowWhen] = useState(false)
  const [showDeadline, setShowDeadline] = useState(false)
  const { t } = useTranslation()
  const taskId = task.id

  // §8.5 — Listen for keyboard shortcut event to open schedule
  useEffect(() => {
    const handleScheduleShortcut = () => setShowWhen(true)
    document.addEventListener(
      TASK_DETAIL_EVENTS.schedule,
      handleScheduleShortcut,
    )
    return () =>
      document.removeEventListener(
        TASK_DETAIL_EVENTS.schedule,
        handleScheduleShortcut,
      )
  }, [])

  // §5.4 — Toggle evening subtype on/off
  const toggleEvening = useCallback(async () => {
    if (!taskId || !startAtData?.startAt) return
    const isEvening = startAtData.subType === StartAtSubType.ThisEvening
    const newSubType = isEvening
      ? StartAtSubType.Default
      : StartAtSubType.ThisEvening
    await scheduleTask(
      taskId,
      String(startAtData.startAt),
      startAtData.type ?? StartAtType.Default,
      newSubType,
    )
  }, [taskId, startAtData])

  const startAtInfo = useMemo(() => {
    const startMs = parseDbDate(startAtData?.startAt)
    if (!startAtData || startMs === undefined) return null

    const startDate = dayjs(startMs)
    const today = dayjs()
    const isSomeday = startAtData.type === StartAtType.Someday
    const isEveningTask = startAtData.subType === StartAtSubType.ThisEvening

    let label: string
    let icon: React.ReactNode
    let iconColor: string

    if (isSomeday) {
      label = t("Someday")
      iconColor = "text-purple-400"
      icon = <CalendarUpcomingIcon className={cn("size-5", iconColor)} />
    } else if (isEveningTask) {
      label = startDate.isSame(today, "day")
        ? t("This evening")
        : startDate.isSame(today.add(1, "day"), "day")
          ? t("Tomorrow evening")
          : t("{{date}} evening", {
              date: startDate.format("ddd, DD MMM").toLowerCase(),
            })
      iconColor = "text-blue-500 dark:text-blue-400"
      icon = <MoonIcon className={cn("size-5 fill-blue-500 dark:fill-blue-400", iconColor)} />
    } else if (startDate.isSame(today, "day") || startDate.isBefore(today, "day")) {
      // Today or past — show "Today" (v1/Things 3 style: task is active now)
      label = t("Today")
      iconColor = "text-yellow-500"
      icon = <StarIcon className={cn("size-5 fill-yellow-500", iconColor)} />
    } else if (startDate.isSame(today.add(1, "day"), "day")) {
      label = t("Tomorrow")
      iconColor = "text-primary"
      icon = <CalendarTodayIcon className={cn("size-5", iconColor)} />
    } else {
      const dayName = startDate.format("ddd").toLowerCase()
      const dayNumber = startDate.format("DD")
      const monthName = startDate.format("MMM").toLowerCase()
      label = `${dayName}, ${dayNumber} ${monthName}`
      iconColor = "text-primary"
      icon = <CalendarTodayIcon className={cn("size-5", iconColor)} />
    }

    return { label, icon, isEvening: isEveningTask }
  }, [startAtData])

  const dueAtInfo = useMemo(() => {
    const dueMs = parseDbDate(task.dueAt)
    if (dueMs === undefined) return null

    const dueDate = dayjs(dueMs)
    const today = dayjs()
    const isOverdue = dueDate.isBefore(today, "day")
    const isDueToday = dueDate.isSame(today, "day")

    const dayName = dueDate.format("ddd").toLowerCase()
    const dayNumber = dueDate.format("DD")
    const monthName = dueDate.format("MMM").toLowerCase()
    const dateLabel = `${dayName}, ${dayNumber} ${monthName}`

    // Relative text — v1/Things 3 style: "Yesterday"/"Today"/"Tomorrow"/"X days left"
    let daysText: string | null = null
    if (isOverdue) {
      const daysOverdue = today.diff(dueDate, "day")
      daysText =
        daysOverdue === 1
          ? t("Yesterday")
          : t("{{count}} days ago", { count: daysOverdue })
    } else if (isDueToday) {
      daysText = t("today")
    } else {
      const daysLeft = dueDate.diff(today, "day")
      if (daysLeft === 1) {
        daysText = t("Tomorrow")
      } else {
        daysText = t("{{count}} days left", { count: daysLeft })
      }
    }

    return {
      dateLabel,
      daysText,
      isOverdue,
      isDueToday,
    }
  }, [task.dueAt])

  const reminderInfo = useMemo(() => {
    const fireMs = parseDbDate(existingReminder?.fireAt)
    if (fireMs === undefined) return null

    const fireDate = dayjs(fireMs)
    const today = dayjs()

    let timeLabel: string
    if (fireDate.isSame(today, "day")) {
      timeLabel = fireDate.format("HH:mm")
    } else if (fireDate.isSame(today.add(1, "day"), "day")) {
      timeLabel = `${t("tomorrow")} ${fireDate.format("HH:mm")}`
    } else {
      timeLabel = `${fireDate.format("ddd")} ${fireDate.format("HH:mm")}`
    }

    return { timeLabel }
  }, [existingReminder])

  return (
    <div>
      {/* Bottom bar — single row: container + dates left, quick-action icons right */}
      <div className="flex items-center justify-between border-t border-border px-4 py-2 mt-5 min-h-10">
        {/* Left: container + dates inline */}
        <div className="flex items-center gap-3 overflow-hidden">
          {/* Container picker — inline, no background */}
          {showContainer && <DetailContainer task={task} />}

          {/* Start date — click opens WhenSheet schedule tab */}
          {startAtInfo && !startAtInfo.isEvening && (
            <button
              onClick={() => setShowWhen(true)}
              className="chip-enter inline-flex min-h-8 cursor-pointer items-center gap-1.5 font-medium transition-opacity hover:opacity-70"
            >
              {startAtInfo.icon}
              <span className="truncate">{startAtInfo.label}</span>
            </button>
          )}

          {/* Evening chip — click toggles evening subtype */}
          {startAtInfo?.isEvening && (
            <button
              onClick={toggleEvening}
              className="chip-enter inline-flex min-h-8 cursor-pointer items-center gap-1.5 font-medium transition-opacity hover:opacity-70"
            >
              {startAtInfo.icon}
              <span className="truncate">{startAtInfo.label}</span>
            </button>
          )}

          {/* Due date — flag icon + date + relative text */}
          {dueAtInfo && (
            <button
              onClick={() => setShowDeadline(true)}
              className={cn(
                "chip-enter inline-flex min-h-8 cursor-pointer items-center gap-1.5 transition-opacity hover:opacity-70",
                dueAtInfo.isOverdue || dueAtInfo.isDueToday
                  ? "text-destructive"
                  : "text-muted-foreground",
              )}
            >
              <FlagIcon className={cn("size-5", dueAtInfo.isOverdue || dueAtInfo.isDueToday ? "text-red-500" : "text-muted-foreground/70")} />
              <span className="truncate">{dueAtInfo.dateLabel}</span>
              {dueAtInfo.daysText && (
                <span className="truncate text-sm font-normal text-muted-foreground/50">
                  {dueAtInfo.daysText}
                </span>
              )}
            </button>
          )}

          {/* Reminder — bell icon + time + count badge */}
          {reminderInfo && (
            <button
              onClick={() => setShowWhen(true)}
              className="chip-enter inline-flex min-h-8 cursor-pointer items-center gap-1 text-muted-foreground/50 transition-opacity hover:opacity-70"
            >
              <NotificationIcon className="size-4" />
              <span className="truncate text-sm">{reminderInfo.timeLabel}</span>
              {reminderCount > 1 && (
                <span className="ml-0.5 inline-flex size-4 items-center justify-center rounded-full bg-muted-foreground/15 text-[9px] font-semibold leading-none text-muted-foreground/70">
                  {reminderCount}
                </span>
              )}
            </button>
          )}
        </div>

        {/* Right: quick-action icons */}
        {quickActions && (
          <div className="flex items-center">{quickActions}</div>
        )}
      </div>

      {/* Schedule picker (startAt) */}
      {taskId && (
        <DatePickerSheet
          open={showWhen}
          onOpenChange={setShowWhen}
          task={task}
          mode="schedule"
          startAtData={startAtData}
          existingReminder={existingReminder}
          allReminders={allReminders}
        />
      )}

      {/* Deadline picker (dueAt) */}
      {taskId && (
        <DatePickerSheet
          open={showDeadline}
          onOpenChange={setShowDeadline}
          task={task}
          mode="deadline"
        />
      )}
    </div>
  )
})
